import React, {useEffect, useState, useRef, useCallback} from "react";
import {Button, Space, message, Select, Card, Row, Col, Tag, Spin, Tooltip} from "antd";
import {EditOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";
import ApiService from "../../core/service/api-service";
import {getStatusColor} from "../../enums/WorklistItemStatusEnum";

export default function WorklistItemBulkScheduleModal({worklistItems, onSuccess, onCancel})
{
    const {t} = useTranslation();
    const [deviceId, setDeviceId] = useState(null);
    const [devices, setDevices] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [patientAppointments, setPatientAppointments] = useState([]);
    const [loadingPatientAppointments, setLoadingPatientAppointments] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [editingAppointmentId, setEditingAppointmentId] = useState(null);
    const [scrollTime, setScrollTime] = useState(moment().format('HH:mm:ss'));
    const [dateRange, setDateRange] = useState({
        start: moment().startOf('week').toDate(),
        end: moment().endOf('week').toDate()
    });
    const calendarRef = useRef(null);

    useEffect(() => {
        loadDevices();
        loadPatientAppointments();
    }, []);

    const loadDevices = async () => {
        try {
            const response = await ApiService.Device.GetList();
            if (response.success) {
                setDevices(response.data.map(x => ({
                    ...x,
                    displayName: `${x.name} (${x.aeTitle}) - ${x.hospital?.name || ''}`
                })));
                setDeviceId(response.data.length > 0 ? response.data[0].id : null);
            }
        } catch (error) {
            console.error('Failed to load devices:', error);
        }
    }

    const loadAppointments = async () => {
        if (!deviceId) return;
        
        try {
            const start = moment(dateRange.start).startOf('day').utc().toISOString();
            const end = moment(dateRange.end).endOf('day').utc().toISOString();

            const response = await ApiService.Appointment.GetByDevice(deviceId, true, start, end);
            if (response.success) {
                const events = response.data.map(appointment => {
                    const item = appointment?.worklistItems?.[0];
                    const isEditing = editingAppointmentId === appointment.id;
                    const color = getStatusColor(appointment.status);
                    return {
                        id: appointment.id,
                        title: `${item?.patientName} ${item?.patientLastname}`,
                        start: appointment.scheduledStartTime,
                        end: appointment.scheduledEndTime,
                        backgroundColor: isEditing ? '#ff9800' : color,
                        borderColor: isEditing ? '#ff9800' : color,
                        extendedProps: {
                            appointment: appointment
                        }
                    }
                });
                setAppointments(events);
            }
        } catch (error) {
            console.error('Failed to load appointments:', error);
        }
    }

    const loadPatientAppointments = async () => {
        if (!worklistItems || worklistItems.length === 0) return;

        // Seçili worklist item'lardan patientId'yi al (hepsi aynı hastaya ait olmalı)
        const patientId = worklistItems[0].patientId;
        if (!patientId) return;

        setLoadingPatientAppointments(true);
        try {
            const response = await ApiService.Appointment.GetScheduledAppointmentsByPatient(patientId, true, true);
            if (response.success) {
                setPatientAppointments(response.data || []);
            }
        } catch (error) {
            console.error('Failed to load patient appointments:', error);
        } finally {
            setLoadingPatientAppointments(false);
        }
    }

    const handleDatesSet = (dateInfo) => {
        const {start, end} = dateInfo;
        
        console.log('Calendar view changed:', dateInfo);
        // Date objelerini timestamp ile karşılaştır
        const prevStart = dateRange.start.getTime();
        const prevEnd = dateRange.end.getTime();
        const newStart = start.getTime();
        const newEnd = end.getTime();
        
        // Eğer tarih aralığı değişmediyse hiçbir şey yapma
        if (prevStart === newStart && prevEnd === newEnd) {
            return;
        }
        
        // State'i güncelle
        setDateRange({
            start: start,
            end: end
        });
    }

    useEffect(() => {
        loadAppointments();
    }, [deviceId, dateRange])

    const handleDateSelect = (selectInfo) => {
        if (!deviceId && !editingAppointmentId) {
            message.warning(t('Please select a device first'));
            return;
        }

        // Scroll pozisyonunu kaydet
        const calendarApi = calendarRef.current?.getApi();
        const scrollContainer = calendarApi?.view?.timeGrid?.scrollerEl ||
            document.querySelector('.fc-scroller-liquid-absolute') ||
            document.querySelector('.fc-scroller');
        const scrollTop = scrollContainer?.scrollTop || 0;

        if (editingAppointmentId) {

            setAppointments(prevAppointments => 
                prevAppointments.map(event => {
                    if (event.id === editingAppointmentId) {
                        return {
                            ...event,
                            start: selectInfo.start,
                            end: selectInfo.end,
                            backgroundColor: '#ff9800',
                            borderColor: '#ff9800'
                        };
                    }
                    return event;
                })
            );

            setPatientAppointments(prevAppointments => 
                prevAppointments.map(appointment => {
                    if (appointment.id === editingAppointmentId) {
                        return {
                            ...appointment,
                            scheduledStartTime: moment(selectInfo.start).format('YYYY-MM-DDTHH:mm:ss'),
                            scheduledEndTime: moment(selectInfo.end).format('YYYY-MM-DDTHH:mm:ss')
                        };
                    }
                    return appointment;
                })
            );

            // Scroll pozisyonunu geri yükle ve takvim seçimini temizle
            setTimeout(() => {
                if (scrollContainer && scrollTop) {
                    scrollContainer.scrollTop = scrollTop;
                }
                // Takvim seçimini temizle
                if (calendarApi) {
                    calendarApi.unselect();
                }
            }, 0);

            setSelectedSlot({
                start: selectInfo.start,
                end: selectInfo.end,
                startStr: selectInfo.startStr,
                endStr: selectInfo.endStr
            });
        } else {
            // Normal mod - yeni randevu için slot seç
            setSelectedSlot({
                start: selectInfo.start,
                end: selectInfo.end,
                startStr: selectInfo.startStr,
                endStr: selectInfo.endStr
            });

            setTimeout(() => {
                if (scrollContainer && scrollTop) {
                    scrollContainer.scrollTop = scrollTop;
                }
                
            }, 0);
        }
    }

    const handleSchedule = async () => {
        // Eğer editing mode'daysa, mevcut randevuya ekle
        if (editingAppointmentId) {
            try {
                // Eğer yeni zaman seçildiyse, randevuyu güncelle
                if (selectedSlot) {
                    const appointment = patientAppointments.find(a => a.id === editingAppointmentId);
                    if (appointment) {
                        const response = await ApiService.WorklistItem.AddToAppointment({
                            appointmentId: editingAppointmentId,
                            worklistItemIds: worklistItems.map(item => item.id),
                            scheduledStartTime: moment(selectedSlot.start).utc().format('YYYY-MM-DDTHH:mm:ss'),
                            scheduledEndTime: moment(selectedSlot.end).utc().format('YYYY-MM-DDTHH:mm:ss')
                        });

                        if (response.success) {
                            message.success(t('Worklist items added to appointment successfully'));
                            if (onSuccess) {
                                onSuccess();
                            }
                        }
                    }

                    return;
                }
                else {
                    const response = await ApiService.WorklistItem.AddToAppointment({
                        appointmentId: editingAppointmentId,
                        worklistItemIds: worklistItems.map(item => item.id),
                    });

                    if (response.success) {
                        message.success(t('Worklist items added to appointment successfully'));
                        if (onSuccess) {
                            onSuccess();
                        }
                    }
                }
                
            } catch (error) {
                console.error('Failed to add to appointment:', error);
                message.error(t('Failed to add to appointment'));
            }
            return;
        }

        // Normal yeni randevu oluşturma modu
        if (!deviceId) {
            message.error(t('Please select a device'));
            return;
        }

        if (!selectedSlot) {
            message.error(t('Please select a time slot on the calendar'));
            return;
        }
        
        try {
            const response = await ApiService.WorklistItem.BulkSchedule({
                worklistItemIds: worklistItems.map(item => item.id),
                deviceId: deviceId,
                scheduledStartTime: moment(selectedSlot.start).utc().format('YYYY-MM-DDTHH:mm:ss'),
                scheduledEndTime: moment(selectedSlot.end).utc().format('YYYY-MM-DDTHH:mm:ss')
            });

            if (response.success) {
                message.success(t('Worklist items scheduled successfully'));
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (error) {
            console.error('Failed to schedule:', error);
        }
    }

    const handleAddToAppointment = (appointmentId) => {
        // Edit moda geç - kullanıcı takvimden zaman seçecek
        handleEditAppointment(appointmentId);
    }

    const handleEditAppointment = (appointmentId) => {
        const appointment = patientAppointments.find(a => a.id === appointmentId);
        if (!appointment) return;

        setEditingAppointmentId(appointmentId);
        setSelectedSlot(null);
        
        // Takvimi randevu tarihine götür
        const appointmentDate = moment(appointment.scheduledStartTime);
        const timeString = appointmentDate.format('HH:mm:ss');
        setScrollTime(timeString);
        
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
            // Tarihe git
            calendarApi.gotoDate(appointmentDate.toDate());
            
            // timeGridWeek veya timeGridDay view'e geç
            calendarApi.changeView('timeGridWeek');
            
            // Randevunun saatine scroll et
            setTimeout(() => {
                const timeString = appointmentDate.format('HH:mm:ss');
                calendarApi.scrollToTime(timeString);
            }, 100);
        }

        // O cihazı seç
        if (appointment.deviceId) {
            setDeviceId(appointment.deviceId);
        }

        // Date range'i güncelle - randevu gününün haftası
        const weekStart = appointmentDate.clone().startOf('week').toDate();
        const weekEnd = appointmentDate.clone().endOf('week').toDate();
        setDateRange({
            start: weekStart,
            end: weekEnd
        });

        message.info(t('Please select a new time slot from the calendar'));
    }

    const handleCancelEdit = () => {
        setEditingAppointmentId(null);
        setSelectedSlot(null);
        // Randevuları yeniden yükle (renkleri eski haline getir)
        loadAppointments();
        loadPatientAppointments();
        message.info(t('Edit cancelled'));
    }

    const handleEventDrop = async (info) => {
        handleDateSelect({
            start: info.event.start,
            end: info.event.end,
            startStr: info.event.startStr,
            endStr: info.event.endStr
        })
    }

    return (
        <div>
            {/* Hastanın Mevcut Randevuları */}
            {patientAppointments.length > 0 && (
                <div style={{marginBottom: 24}}>
                    <h3 style={{marginBottom: 12}}>{t("Patient's Existing Appointments")}</h3>
                    <Spin spinning={loadingPatientAppointments}>
                        <Row gutter={[16, 16]}>
                            {patientAppointments.map(appointment => (
                                <Col xs={24} sm={12} md={8} lg={6} key={appointment.id}>
                                    <Card
                                        size="small"
                                        style={{
                                            border: editingAppointmentId === appointment.id ? '2px solid #ff9800' : '1px solid #d9d9d9',
                                            borderRadius: 8,
                                            backgroundColor: editingAppointmentId === appointment.id ? '#fff3e0' : 'white',
                                            boxShadow: editingAppointmentId === appointment.id ? '0 4px 12px rgba(255, 152, 0, 0.2)' : 'none'
                                        }}
                                    >
                                        <div style={{marginBottom: 8}}>
                                            <strong>{appointment.device?.name || 'N/A'}</strong>
                                            <div style={{fontSize: 12, color: '#666'}}>
                                                {appointment.device?.hospital?.name || 'N/A'}
                                            </div>
                                        </div>
                                        <div style={{marginBottom: 8, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                            <div>{moment(appointment.scheduledStartTime).format('DD-MM-YYYY HH:mm')} - {moment(appointment.scheduledEndTime).format('DD-MM-YYYY HH:mm')}</div>
                                        </div>
                                        <div style={{display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12}}>
                                            {
                                                appointment?.worklistItems?.map(w => (
                                                    <div style={{ marginBottom: 8, fontSize: 12 }}>
                                                        <Tag color="blue">{w?.requestType?.displayName} </Tag>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                        {editingAppointmentId === appointment.id ? (
                                            <Space direction="vertical" style={{width: '100%'}}>
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    block
                                                    disabled
                                                >
                                                    {t('Select time from calendar')}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    block
                                                    onClick={handleCancelEdit}
                                                >
                                                    {t('Cancel Edit')}
                                                </Button>
                                            </Space>
                                        ) : (
                                            <Button
                                                type="primary"
                                                size="small"
                                                block
                                                onClick={() => handleAddToAppointment(appointment.id)}
                                                disabled={editingAppointmentId !== null}
                                            >
                                                {t('Add to This Appointment')}
                                            </Button>
                                        )}
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Spin>
                    <div style={{
                        margin: '16px 0',
                        borderTop: '1px solid #d9d9d9',
                        paddingTop: 16
                    }}>
                        <h4>{t('Or Create New Appointment')}</h4>
                    </div>
                </div>
            )}

            <Space direction="vertical" style={{width: '100%', marginBottom: 16}}>
                <div>
                    <label style={{display: 'block', marginBottom: 8, fontWeight: 'bold'}}>
                        {t('Device')} *
                    </label>
                    <Select
                        disabled={!!editingAppointmentId}
                        value={deviceId}
                        onChange={setDeviceId}
                        style={{width: '100%'}}
                        placeholder={t('Select Device')}
                        options={devices}
                        fieldNames={{
                            label: 'displayName',
                            value: 'id'
                        }}
                        showSearch
                        filterOption={(input, option) => {
                            return (option?.displayName ?? '').toUpperCase().includes(input.toUpperCase())
                        }}
                    />
                </div>
            </Space>

            <div style={{marginBottom: 16}}>
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
                    }}
                    editable={true}
                    eventDurationEditable={true}
                    eventDrop={handleEventDrop}
                    eventResize={handleEventDrop}
                    selectable={true}
                    selectMirror={true}
                    dayMaxEvents={true}
                    scrollTime={scrollTime}
                    weekends={true}
                    events={appointments}
                    select={handleDateSelect}
                    datesSet={handleDatesSet}
                    height="500px"
                    slotDuration={'00:15:00'}
                    slotLabelInterval={'00:15:00'}
                    slotLabelFormat={{
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false, // AM/PM'i kapatır, 24 saat formatına geçer
                        meridiem: false // "am/pm" yazısını gizler
                    }}
                    allDaySlot={false}
                    locale="tr"
                    buttonText={{
                        today: t('Today'),
                        week: t('Week'),
                        day: t('Day')
                    }}
                    validRange={{
                        start: moment().startOf('day').toDate()
                    }}
                    selectAllow={(selectInfo) => {
                        // Only allow future dates
                        return selectInfo.start >= new Date();
                    }}
                />
            </div>

            {selectedSlot && (
                <div style={{
                    padding: 12,
                    backgroundColor: editingAppointmentId ? '#fff3e0' : '#e6f7ff',
                    border: editingAppointmentId ? '1px solid #ff9800' : '1px solid #91d5ff',
                    borderRadius: 4,
                    marginBottom: 8
                }}>
                    <div><strong>{t('Selected Time Slot')}:</strong></div>
                    <div>{moment(selectedSlot.start).format('DD.MM.YYYY HH:mm')} - {moment(selectedSlot.end).format('DD.MM.YYYY HH:mm')}</div>
                </div>
            )}

            <div style={{textAlign: 'right'}}>
                <Space>
                    <Button onClick={() => onCancel && onCancel()}>
                        {t('Cancel')}
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={handleSchedule}
                        disabled={editingAppointmentId ? false : (!deviceId || !selectedSlot)}
                    >
                        {editingAppointmentId 
                            ? t('Add to Appointment') + ` (${worklistItems?.length} ${t('items')})`
                            : t('Schedule') + ` (${worklistItems?.length} ${t('items')})`
                        }
                    </Button>
                </Space>
            </div>
        </div>
    )
}
